import { useCallback, useSyncExternalStore } from 'react';
import { useRecoilValue } from 'recoil';
import type { CustomTargetObject } from '~/engine/data/common/customs/CustomTargetObject';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { couchModelPreviewSelectionState } from '~/state/atoms/couchTopAtoms';
import { operationPanelState } from '~/state/atoms/toolsAtoms';

/** How the operation preview on screen changes the viewport. */
export interface OperationPreviewDisplay {
    /** The other structures are dimmed while the preview shows. */
    readonly dimsOtherStructures: boolean;
    /** The selected structure is hidden, and the preview shows its result. */
    readonly hidesPreviewedStructure: boolean;
}

/**
 * The operation preview the edit target's Environment draws into the image,
 * the one every operation previews in, or undefined while there is none,
 * followed through {@link Environment.subscribeToOperationPreview}.
 */
function useEnvironmentOperationPreview(): CustomTargetObject | undefined {
    const { environment } = useActiveEditTarget();
    const subscribe = useCallback(
        (onChange: () => void) =>
            environment?.subscribeToOperationPreview(onChange) ?? (() => {}),
        [environment]
    );
    return useSyncExternalStore(
        subscribe,
        () => environment?.operationPreviewStructure
    );
}

/**
 * The operation preview on screen, or undefined while there is none: the
 * result the edit target's Environment draws, or the couch model the open couch
 * panel draws, which changes nothing else on screen.
 */
export function useOperationPreviewDisplay():
    | OperationPreviewDisplay
    | undefined {
    const preview = useEnvironmentOperationPreview();
    const couchPreview = useRecoilValue(couchModelPreviewSelectionState);
    const isCouchPanelOpen =
        useRecoilValue(operationPanelState).type === 'couch';
    if (preview) {
        return {
            dimsOtherStructures: preview.dimsOtherStructures,
            hidesPreviewedStructure: preview.hidesPreviewedStructure,
        };
    }
    if (couchPreview && isCouchPanelOpen) {
        return { dimsOtherStructures: false, hidesPreviewedStructure: false };
    }
    return undefined;
}
